import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { inlineError, type InlineError } from '@/lib/errorReport';
import type { QuoteReviewState } from '@/types/review';

export function useQuoteReview(quoteId: string) {
  const [state, setState] = useState<QuoteReviewState | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<InlineError | null>(null);
  /** `productId::groep` van de keuzegroep die nu wordt opgeslagen. */
  const [assigning, setAssigning] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await api.getQuoteReviewState(quoteId);
      setState(next);
      setError(null);
      return next;
    } catch (cause: unknown) {
      const data = (cause as { response?: { data?: { message?: string; error?: string } } })
        ?.response?.data;
      setError(inlineError(data?.message || data?.error || 'Reviewstatus kon niet worden geladen', cause));
      return null;
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (state?.status !== 'pending') return;
    const timer = window.setInterval(() => void refresh(), 20_000);
    return () => window.clearInterval(timer);
  }, [refresh, state?.status]);

  /** Wijst de verantwoordelijke voor één keuzegroep aan en herlaadt de status. */
  const assignReviewer = useCallback(
    async (productId: string, groupId: string, ownerId: string | null) => {
      setAssigning(`${productId}::${groupId}`);
      setError(null);
      try {
        const next = await api.setQuoteReviewAssignments(quoteId, [
          { productId, groupId, ownerId },
        ]);
        setState(next);
        return next;
      } catch (cause: unknown) {
        const data = (cause as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data;
        setError(
          inlineError(data?.message || data?.error || 'Verantwoordelijke kon niet worden opgeslagen', cause)
        );
        return null;
      } finally {
        setAssigning(null);
      }
    },
    [quoteId],
  );

  const requestReview = useCallback(async (beforeRequest: () => Promise<boolean>) => {
    setRequesting(true);
    setError(null);
    try {
      if (!(await beforeRequest())) {
        setError(inlineError('Sla de laatste wijzigingen op en probeer opnieuw.'));
        return null;
      }
      const next = await api.requestQuoteReview(quoteId);
      setState(next);
      return next;
    } catch (cause: unknown) {
      const data = (cause as { response?: { data?: { message?: string; error?: string } } })
        ?.response?.data;
      setError(inlineError(data?.message || data?.error || 'Reviewaanvraag kon niet worden verstuurd', cause));
      return null;
    } finally {
      setRequesting(false);
    }
  }, [quoteId]);

  return { state, loading, requesting, assigning, error, refresh, requestReview, assignReviewer };
}

export default useQuoteReview;
